import { fabric } from 'fabric';

import type { ArtboardSize } from './types';

/**
 * Smart guides: while a layer is dragged, its edges and centre snap to the
 * artboard's edges and centre lines and to every other layer's, and the line
 * it snapped to is drawn across the canvas. Alt disables snapping for the drag.
 *
 * Guides are painted after Fabric's render on the main context, only while a
 * drag is live, and never when Fabric renders for export (interactive=false).
 */
export interface SnapHandle {
    detach: () => void;
}

interface Guide { axis: 'x' | 'y'; at: number; from: number; to: number }

const GUIDE_COLOR = '#F43F5E';
const THRESHOLD_PX = 6;

export function attachSnapping(
    canvas: fabric.Canvas,
    getArtboard: () => ArtboardSize,
    isEnabled: () => boolean,
): SnapHandle {
    let guides: Guide[] = [];

    const bounds = (o: fabric.Object) => {
        const r = o.getBoundingRect(true, true);
        return { l: r.left, t: r.top, r: r.left + r.width, b: r.top + r.height, cx: r.left + r.width / 2, cy: r.top + r.height / 2 };
    };

    const onMoving = (e: fabric.IEvent) => {
        const target = e.target as fabric.Object | undefined;
        guides = [];
        if (!target || !isEnabled() || (e.e as MouseEvent)?.altKey) return;
        const zoom = canvas.getZoom() || 1;
        const thr = THRESHOLD_PX / zoom;
        const art = getArtboard();
        target.setCoords();
        const me = bounds(target);
        const moving = new Set<fabric.Object>(
            target.type === 'activeSelection' ? (target as fabric.ActiveSelection).getObjects() : [target],
        );
        const xs: Array<{ at: number; from: number; to: number }> = [
            { at: 0, from: 0, to: art.h }, { at: art.w / 2, from: 0, to: art.h }, { at: art.w, from: 0, to: art.h },
        ];
        const ys: Array<{ at: number; from: number; to: number }> = [
            { at: 0, from: 0, to: art.w }, { at: art.h / 2, from: 0, to: art.w }, { at: art.h, from: 0, to: art.w },
        ];
        canvas.getObjects().forEach((o) => {
            if (moving.has(o) || !o.visible || (o as any).csTemp) return;
            // A full-bleed background repeats the artboard's own lines.
            if ((o as any).csRole === 'background') return;
            const b = bounds(o);
            [b.l, b.cx, b.r].forEach((x) => xs.push({ at: x, from: b.t, to: b.b }));
            [b.t, b.cy, b.b].forEach((y) => ys.push({ at: y, from: b.l, to: b.r }));
        });

        const best = (mine: number[], lines: Array<{ at: number }>) => {
            let delta = Infinity;
            for (const v of mine) {
                for (const l of lines) {
                    const d = l.at - v;
                    if (Math.abs(d) < thr && Math.abs(d) < Math.abs(delta)) delta = d;
                }
            }
            return Number.isFinite(delta) ? delta : 0;
        };
        const dx = best([me.l, me.cx, me.r], xs);
        const dy = best([me.t, me.cy, me.b], ys);
        if (dx || dy) {
            target.set({ left: (target.left || 0) + dx, top: (target.top || 0) + dy });
            target.setCoords();
        }
        const snapped = bounds(target);
        const eps = 0.5 / zoom;
        for (const v of [snapped.l, snapped.cx, snapped.r]) {
            const hits = xs.filter((l) => Math.abs(l.at - v) < eps);
            if (hits.length) {
                const from = Math.min(snapped.t, ...hits.map((l) => l.from));
                const to = Math.max(snapped.b, ...hits.map((l) => l.to));
                guides.push({ axis: 'x', at: v, from, to });
            }
        }
        for (const v of [snapped.t, snapped.cy, snapped.b]) {
            const hits = ys.filter((l) => Math.abs(l.at - v) < eps);
            if (hits.length) {
                const from = Math.min(snapped.l, ...hits.map((l) => l.from));
                const to = Math.max(snapped.r, ...hits.map((l) => l.to));
                guides.push({ axis: 'y', at: v, from, to });
            }
        }
    };

    const clear = () => {
        if (guides.length) {
            guides = [];
            canvas.requestRenderAll();
        }
    };

    const onAfterRender = (e: any) => {
        if (!guides.length || !(canvas as any).interactive) return;
        const ctx: CanvasRenderingContext2D = e.ctx || (canvas as any).contextContainer;
        if (!ctx) return;
        const v = canvas.viewportTransform || [1, 0, 0, 1, 0, 0];
        ctx.save();
        ctx.strokeStyle = GUIDE_COLOR;
        ctx.lineWidth = 1;
        ctx.setLineDash([]);
        for (const g of guides) {
            ctx.beginPath();
            if (g.axis === 'x') {
                const x = Math.round(g.at * v[0] + v[4]) + 0.5;
                ctx.moveTo(x, g.from * v[3] + v[5]);
                ctx.lineTo(x, g.to * v[3] + v[5]);
            } else {
                const y = Math.round(g.at * v[3] + v[5]) + 0.5;
                ctx.moveTo(g.from * v[0] + v[4], y);
                ctx.lineTo(g.to * v[0] + v[4], y);
            }
            ctx.stroke();
        }
        ctx.restore();
    };

    canvas.on('object:moving', onMoving);
    canvas.on('mouse:up', clear);
    canvas.on('selection:cleared', clear);
    canvas.on('after:render', onAfterRender);
    return {
        detach: () => {
            canvas.off('object:moving', onMoving as any);
            canvas.off('mouse:up', clear as any);
            canvas.off('selection:cleared', clear as any);
            canvas.off('after:render', onAfterRender as any);
        },
    };
}
